package co.uk.paxes.pinpoint.web

import kotlinx.browser.window

/**
 * H5AdManager manages AdSense H5 Games Ads.
 * It provides a Kotlin interface to call the JavaScript 'adBreak' and 'adConfig' functions.
 * Enhanced with ad availability checking and user feedback for better UX.
 */
object H5AdManager {
    
    // Frequency throttling for interstitials - only show every 2nd call
    private var nextBreakCallCount = 0
    private const val NEXT_BREAK_FREQUENCY = 2  // Show ad every 2 hands
    
    // Ad availability tracking
    private var lastAdAvailabilityCheck: Long = 0
    private var isAdAvailable: Boolean = true
    private const val AD_AVAILABILITY_CACHE_MS = 30000  // Cache for 30 seconds
    
    /**
     * Check if ads are currently available from AdSense.
     * Uses caching to avoid excessive checks.
     * @return true if ads are available, false otherwise
     */
    fun checkAdAvailability(): Boolean {
        val now = kotlin.js.Date.now().toLong()
        
        // Use cached result if recent
        if (now - lastAdAvailabilityCheck < AD_AVAILABILITY_CACHE_MS) {
            console.log("H5AdManager: Using cached ad availability: $isAdAvailable")
            return isAdAvailable
        }
        
        // Check if AdSense API is available
        val w = window.asDynamic()
        if (w.adBreak == null) {
            console.log("H5AdManager: AdSense API not available")
            isAdAvailable = false
            lastAdAvailabilityCheck = now
            return false
        }
        
        // For now, assume ads are available if API exists
        // In a real implementation, you might check additional AdSense status
        isAdAvailable = true
        lastAdAvailabilityCheck = now
        console.log("H5AdManager: Ad availability checked: $isAdAvailable")
        return isAdAvailable
    }
    
    /**
     * Display a user-friendly message when ads are not available.
     * Shows a temporary notification to inform the user.
     */
    fun displayNoAdsMessage() {
        console.log("H5AdManager: Displaying no ads available message")
        
        // Create a temporary notification element
        val notification = kotlinx.browser.document.createElement("div")
        notification.className = "ad-unavailable-notification"
        notification.textContent = "No ads available right now, try again later"
        
        // Style the notification
        val style = notification.asDynamic().style
        style.position = "fixed"
        style.top = "20px"
        style.left = "50%"
        style.transform = "translateX(-50%)"
        style.backgroundColor = "#ff6b6b"
        style.color = "white"
        style.padding = "12px 24px"
        style.borderRadius = "8px"
        style.fontSize = "14px"
        style.fontWeight = "bold"
        style.zIndex = "10000"
        style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)"
        style.animation = "fadeInOut 3s ease-in-out"
        
        // Add CSS animation if not already present
        if (kotlinx.browser.document.getElementById("ad-notification-styles") == null) {
            val styleSheet = kotlinx.browser.document.createElement("style")
            styleSheet.id = "ad-notification-styles"
            styleSheet.textContent = """
                @keyframes fadeInOut {
                    0% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
                    20% { opacity: 1; transform: translateX(-50%) translateY(0); }
                    80% { opacity: 1; transform: translateX(-50%) translateY(0); }
                    100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
                }
            """.trimIndent()
            kotlinx.browser.document.head?.appendChild(styleSheet)
        }
        
        // Add to page
        kotlinx.browser.document.body?.appendChild(notification)
        
        // Remove after 3 seconds
        kotlinx.browser.window.setTimeout({
            try {
                kotlinx.browser.document.body?.removeChild(notification)
            } catch (e: Exception) {
                console.log("H5AdManager: Notification already removed")
            }
        }, 3000)
    }
    
    /**
     * Enhanced ad break trigger with availability checking and user feedback.
     * Checks ad availability before attempting to show ads and provides feedback if unavailable.
     */
    fun triggerAdBreakWithFeedback(
        type: String,
        name: String,
        onBeforeAd: (() -> Unit)? = null,
        onAfterAd: (() -> Unit)? = null,
        onAdDismissed: (() -> Unit)? = null,
        onAdViewed: (() -> Unit)? = null,
        onBeforeReward: ((() -> Unit) -> Unit)? = null,
        onAdBreakDone: ((dynamic) -> Unit)? = null,
        onAdUnavailable: (() -> Unit)? = null
    ) {
        console.log("H5AdManager: 🎯 triggerAdBreakWithFeedback called for type=$type, name=$name")
        
        val w = window.asDynamic()
        if (w.adBreak == null) {
            console.log("H5AdManager: ❌ AdSense API not available")
            displayNoAdsMessage()
            onAdUnavailable?.invoke()
            onAfterAd?.invoke()
            return
        }
        
        console.log("H5AdManager: ✅ AdSense API available, setting up timeout logic")
        
        // Set up a timeout to handle cases where AdSense doesn't respond
        var timeoutId: Int? = null
        var callbackReceived = false
        
        val handleTimeout = {
            if (!callbackReceived) {
                console.log("H5AdManager: ⏰ TIMEOUT - Ad request timed out after 2 seconds - no response from AdSense")
                callbackReceived = true
                displayNoAdsMessage()
                console.log("H5AdManager: About to call onAdUnavailable callback")
                console.log("H5AdManager: onAdUnavailable is null: ${onAdUnavailable == null}")
                try {
                    onAdUnavailable?.invoke()
                    console.log("H5AdManager: ✅ onAdUnavailable callback invoked successfully")
                } catch (e: Exception) {
                    console.log("H5AdManager: ❌ Exception in onAdUnavailable callback: ${e.message}")
                    console.error("H5AdManager: onAdUnavailable callback error:", e)
                }
                console.log("H5AdManager: About to call onAfterAd callback")
                console.log("H5AdManager: onAfterAd is null: ${onAfterAd == null}")
                try {
                    onAfterAd?.invoke()
                    console.log("H5AdManager: ✅ onAfterAd callback invoked successfully")
                } catch (e: Exception) {
                    console.log("H5AdManager: ❌ Exception in onAfterAd callback: ${e.message}")
                    console.error("H5AdManager: onAfterAd callback error:", e)
                }
                console.log("H5AdManager: Timeout handling complete")
            }
        }
        
        // Set timeout for 2 seconds
        timeoutId = window.setTimeout(handleTimeout, 2000)
        console.log("H5AdManager: ⏰ Timeout set for 2 seconds (ID: $timeoutId)")
        
        // Wrap callbacks to clear timeout and prevent double-calling
        val wrappedOnBeforeAd: (() -> Unit)? = if (onBeforeAd != null) {
            {
                console.log("H5AdManager: 🎯 wrappedOnBeforeAd called, callbackReceived=$callbackReceived")
                if (!callbackReceived) {
                    callbackReceived = true
                    timeoutId?.let { 
                        console.log("H5AdManager: 🎯 Clearing timeout $it")
                        window.clearTimeout(it) 
                    }
                    console.log("H5AdManager: Callback beforeAd")
                    onBeforeAd.invoke()
                }
            }
        } else null
        
        val wrappedOnAfterAd: (() -> Unit)? = if (onAfterAd != null) {
            {
                console.log("H5AdManager: 🎯 wrappedOnAfterAd called, callbackReceived=$callbackReceived")
                if (!callbackReceived) {
                    callbackReceived = true
                    timeoutId?.let { 
                        console.log("H5AdManager: 🎯 Clearing timeout $it")
                        window.clearTimeout(it) 
                    }
                }
                console.log("H5AdManager: Callback afterAd")
                onAfterAd.invoke()
            }
        } else null
        
        val wrappedOnAdDismissed: (() -> Unit)? = if (onAdDismissed != null) {
            {
                console.log("H5AdManager: 🎯 wrappedOnAdDismissed called, callbackReceived=$callbackReceived")
                if (!callbackReceived) {
                    callbackReceived = true
                    timeoutId?.let { 
                        console.log("H5AdManager: 🎯 Clearing timeout $it")
                        window.clearTimeout(it) 
                    }
                }
                console.log("H5AdManager: Callback adDismissed")
                onAdDismissed.invoke()
            }
        } else null
        
        val wrappedOnAdViewed: (() -> Unit)? = if (onAdViewed != null) {
            {
                console.log("H5AdManager: 🎯 wrappedOnAdViewed called, callbackReceived=$callbackReceived")
                if (!callbackReceived) {
                    callbackReceived = true
                    timeoutId?.let { 
                        console.log("H5AdManager: 🎯 Clearing timeout $it")
                        window.clearTimeout(it) 
                    }
                }
                console.log("H5AdManager: Callback adViewed")
                onAdViewed.invoke()
            }
        } else null
        
        // Proceed with normal ad break flow with wrapped callbacks
        triggerAdBreak(
            type = type,
            name = name,
            onBeforeAd = wrappedOnBeforeAd,
            onAfterAd = wrappedOnAfterAd,
            onAdDismissed = wrappedOnAdDismissed,
            onAdViewed = wrappedOnAdViewed,
            onBeforeReward = onBeforeReward,
            onAdBreakDone = onAdBreakDone
        )
    }

    /**
     * Trigger an ad break.
     * @param type The type of ad break (e.g., 'next', 'interstitial', 'reward', 'preroll')
     * @param name A unique name for this ad break location
     * @param onBeforeAd Called before the ad starts
     * @param onAfterAd Called after the ad ends (regardless of success)
     * @param onAdDismissed Called if the user dismisses the ad
     * @param onAdViewed Called if the user views the ad completely (for rewards)
     */
    fun triggerAdBreak(
        type: String,
        name: String,
        onBeforeAd: (() -> Unit)? = null,
        onAfterAd: (() -> Unit)? = null,
        onAdDismissed: (() -> Unit)? = null,
        onAdViewed: (() -> Unit)? = null,
        onBeforeReward: ((() -> Unit) -> Unit)? = null,
        onAdBreakDone: ((dynamic) -> Unit)? = null
    ) {
        val w = window.asDynamic()
        if (w.adBreak != null) {
            console.log("H5AdManager: 🚀 Triggering adBreak type=$type, name=$name")
            
            val config = js("{}")
            config.type = type
            config.name = name
            
            config.beforeAd = {
                console.log("H5AdManager: Callback beforeAd")
                onBeforeAd?.invoke()
            }
            
            config.afterAd = {
                console.log("H5AdManager: Callback afterAd")
                onAfterAd?.invoke()
            }
            
            config.adBreakDone = { placementInfo: dynamic ->
                console.log("H5AdManager: Callback adBreakDone", placementInfo)
                onAdBreakDone?.invoke(placementInfo)
            }
            
            config.adDismissed = {
                console.log("H5AdManager: Callback adDismissed")
                onAdDismissed?.invoke()
            }
            
            config.adViewed = {
                console.log("H5AdManager: Callback adViewed")
                onAdViewed?.invoke()
            }
            
            // Special handling for rewarded ads 'beforeReward' callback
            if (onBeforeReward != null) {
                config.beforeReward = { showAdFn: dynamic ->
                    console.log("H5AdManager: Callback beforeReward (custom)")
                    onBeforeReward { showAdFn() }
                }
            } else if (type == "reward") {
                // Default behavior for rewards: show immediately if no custom logic provided
                config.beforeReward = { showAdFn: dynamic ->
                    console.log("H5AdManager: Callback beforeReward (default) - showing ad")
                    showAdFn()
                }
            }
            
            w.adBreak(config)
        } else {
            console.log("H5AdManager: ⚠️ adBreak is not defined on window. AdSense H5 might not be initialized yet.")
            // Update availability cache
            isAdAvailable = false
            lastAdAvailabilityCheck = kotlin.js.Date.now().toLong()
            // Fallback: call onAfterAd immediately if provided
            onAfterAd?.invoke()
        }
    }
    
    /**
     * Trigger a 'next' ad break (interstitial between levels/hands).
     * Throttled to only show every NEXT_BREAK_FREQUENCY calls to comply with AdSense policies.
     */
    fun triggerNextBreak(name: String, onComplete: (() -> Unit)? = null) {
        nextBreakCallCount++
        
        if (nextBreakCallCount >= NEXT_BREAK_FREQUENCY) {
            console.log("H5AdManager: Triggering interstitial (call #$nextBreakCallCount)")
            nextBreakCallCount = 0  // Reset counter
            // Use regular triggerAdBreak for interstitials (no timeout/feedback needed)
            triggerAdBreak(
                type = "next",
                name = name,
                onAfterAd = onComplete
            )
        } else {
            console.log("H5AdManager: Skipping interstitial (call #$nextBreakCallCount/$NEXT_BREAK_FREQUENCY)")
            // Call onComplete immediately if provided
            onComplete?.invoke()
        }
    }
}
